import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { docsForRole, ROLE_LABELS, ROLE_MATRIX } from "../docs/content";
import {
  outlinedButtonSx,
  panelSx,
  secondaryButtonSx,
  sectionLeadSx,
  sectionTitleSx,
  shellCardSx,
  uiMotion,
  uiRadii,
} from "../uiStyles";

const mediaUrl = (name) => `${process.env.PUBLIC_URL || ""}/docs/${name}`;
const ROLE_ORDER = ["admin", "owner", "manager", "executor"];

const mediaFrameSx = {
  display: "block",
  width: "100%",
  borderRadius: uiRadii.card,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: (theme) => theme.palette.ui.shadowPanel,
  bgcolor: "ui.subtle",
};

function DocNav({ sections, activeId, onSelect }) {
  return (
    <Stack spacing={0.5}>
      {sections.map((section) => {
        const active = section.id === activeId;
        return (
          <Button
            key={section.id}
            type="button"
            onClick={() => onSelect(section.id)}
            sx={{
              ...secondaryButtonSx,
              position: "relative",
              justifyContent: "flex-start",
              borderRadius: 3,
              minHeight: 42,
              color: active ? "primary.contrastText" : "text.primary",
              "&:hover": { bgcolor: active ? "transparent" : "action.hover" },
            }}
          >
            {active && (
              <Box
                component={motion.span}
                layoutId="docs-nav"
                transition={{ type: "spring", stiffness: 420, damping: 36 }}
                sx={{ position: "absolute", inset: 0, borderRadius: 3, bgcolor: "primary.main", zIndex: 0 }}
              />
            )}
            <Box component="span" sx={{ position: "relative", zIndex: 1 }}>{section.title}</Box>
          </Button>
        );
      })}
    </Stack>
  );
}

function DocImage({ src, caption, onZoom }) {
  return (
    <Box component="figure" sx={{ m: 0 }}>
      <Box
        component="img"
        src={mediaUrl(src)}
        alt={caption}
        loading="lazy"
        onClick={() => onZoom({ src, caption })}
        sx={{ ...mediaFrameSx, cursor: "zoom-in", transition: "transform 0.25s ease", "&:hover": { transform: "translateY(-2px)" } }}
      />
      {caption && (
        <Typography component="figcaption" variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}

function DocVideo({ src, poster, caption }) {
  const videoRef = useRef(null);
  // Play only while on screen: a section may hold several looping clips.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0.35 });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  return (
    <Box component="figure" sx={{ m: 0 }}>
      <Box
        component="video"
        ref={videoRef}
        src={mediaUrl(src)}
        poster={poster ? mediaUrl(poster) : undefined}
        muted
        loop
        playsInline
        preload="metadata"
        sx={mediaFrameSx}
      />
      {caption && (
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mt: 1, color: "text.secondary" }}>
          <PlayCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />
          <Typography component="figcaption" variant="body2">{caption}</Typography>
        </Stack>
      )}
    </Box>
  );
}

function RoleMatrix({ role }) {
  const column = ROLE_ORDER.indexOf(role) + 1;
  return (
    <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: uiRadii.card }}>
      <Table size="small" sx={{ minWidth: 560, "& td, & th": { borderColor: "divider" } }}>
        <TableHead>
          <TableRow>
            <TableCell />
            {ROLE_ORDER.map((key, index) => (
              <TableCell key={key} align="center" sx={{ fontWeight: 600, bgcolor: index + 1 === column ? "action.selected" : "transparent" }}>
                {ROLE_LABELS[key]}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROLE_MATRIX.map(([label, ...values]) => (
            <TableRow key={label}>
              <TableCell sx={{ color: "text.secondary" }}>{label}</TableCell>
              {values.map((value, index) => (
                <TableCell key={index} align="center" sx={{ bgcolor: index + 1 === column ? "action.selected" : "transparent", fontWeight: index + 1 === column ? 600 : 400 }}>
                  {value}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function DocBlock({ block, role, onZoom }) {
  if (block.type === "h") return <Typography variant="h6" sx={{ ...sectionTitleSx, pt: 1 }}>{block.text}</Typography>;
  if (block.type === "p") return <Typography sx={{ lineHeight: 1.7 }}>{block.text}</Typography>;
  if (block.type === "image") return <DocImage src={block.src} caption={block.caption} onZoom={onZoom} />;
  if (block.type === "video") return <DocVideo src={block.src} poster={block.poster} caption={block.caption} />;
  if (block.type === "roles") return <RoleMatrix role={role} />;
  if (block.type === "tip") {
    return (
      <Stack direction="row" spacing={1.25} sx={{ p: 1.5, borderRadius: uiRadii.card, bgcolor: "ui.subtle", border: "1px solid", borderColor: "divider" }}>
        <LightbulbOutlinedIcon sx={{ color: "warning.main", fontSize: 20, mt: 0.25 }} />
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{block.text}</Typography>
      </Stack>
    );
  }
  const ordered = block.type === "steps";
  return (
    <Box component={ordered ? "ol" : "ul"} sx={{ m: 0, pl: 3, "& li": { lineHeight: 1.7, mb: 0.5, pl: 0.5 } }}>
      {block.items.map((item) => <li key={item}>{item}</li>)}
    </Box>
  );
}

export default function DocsPage({ role = "executor", onBack }) {
  const sections = useMemo(() => docsForRole(role), [role]);
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const [zoom, setZoom] = useState(null);
  const index = Math.max(0, sections.findIndex((section) => section.id === activeId));
  const section = sections[index];
  const select = (id) => {
    setActiveId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: uiMotion.base, ease: uiMotion.ease }}
      sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pb: 3, mt: { xs: 2, md: 3 }, flex: 1, display: "flex" }}
    >
      <Paper
        elevation={0}
        sx={{
          ...shellCardSx,
          p: { xs: 1.5, md: 2 },
          display: "flex",
          gap: 2.5,
          width: "100%",
          alignItems: "flex-start",
          flexDirection: "column",
          "@media (min-width:1100px)": { flexDirection: "row" },
        }}
      >
        <Box sx={{ width: "100%", flexShrink: 0, "@media (min-width:1100px)": { width: 260, position: "sticky", top: 92 } }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Справка</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Для роли «{ROLE_LABELS[role] || role}»
            </Typography>
            <Box sx={{ display: "none", "@media (min-width:1100px)": { display: "block" } }}>
              <DocNav sections={sections} activeId={section?.id} onSelect={select} />
            </Box>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ "@media (min-width:1100px)": { display: "none" } }}>
              {sections.map((item) => (
                <Chip
                  key={item.id}
                  label={item.title}
                  onClick={() => select(item.id)}
                  color={item.id === section?.id ? "primary" : "default"}
                  variant={item.id === section?.id ? "filled" : "outlined"}
                />
              ))}
            </Stack>
            <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackRoundedIcon />} sx={{ ...outlinedButtonSx, mt: 2 }}>
              Назад
            </Button>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%", px: { xs: 0, md: 2, xl: 4 } }}>
          <AnimatePresence mode="wait" initial={false}>
            {section && (
              <Paper
                key={section.id}
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: uiMotion.fast, ease: uiMotion.ease }}
                elevation={0}
                sx={{ ...panelSx, maxWidth: 980, mx: "auto" }}
              >
                <Typography variant="h5" sx={{ mb: 0.5 }}>{section.title}</Typography>
                <Typography variant="body2" sx={sectionLeadSx}>
                  Раздел {index + 1} из {sections.length}
                </Typography>
                <Stack spacing={2.25}>
                  {section.blocks.map((block, blockIndex) => (
                    <Box
                      key={blockIndex}
                      component={motion.div}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(blockIndex, 8) * 0.04, duration: uiMotion.base, ease: uiMotion.ease }}
                    >
                      <DocBlock block={block} role={role} onZoom={setZoom} />
                    </Box>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Box>
                    {index > 0 && (
                      <Button onClick={() => select(sections[index - 1].id)} startIcon={<ArrowBackRoundedIcon />} sx={secondaryButtonSx}>
                        {sections[index - 1].title}
                      </Button>
                    )}
                  </Box>
                  <Box>
                    {index < sections.length - 1 && (
                      <Button onClick={() => select(sections[index + 1].id)} endIcon={<ArrowForwardRoundedIcon />} sx={outlinedButtonSx} variant="outlined">
                        {sections[index + 1].title}
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}
          </AnimatePresence>
        </Box>
      </Paper>
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="xl" fullWidth>
        {zoom && (
          <Box component="img" src={mediaUrl(zoom.src)} alt={zoom.caption} onClick={() => setZoom(null)} sx={{ display: "block", width: "100%", cursor: "zoom-out" }} />
        )}
      </Dialog>
    </Box>
  );
}
